import getFixedFeeTaxPercent from 'utils/purchase/getFixedFeeTaxPercent';

/** Translation key of the legal legend that must accompany the fee rows. */
export const FIXED_FEE_LEGEND_KEY = 'fixed_fee.legend';

/**
 * Builds the disclosure rows of the fixed platform-usage fee (MID-747).
 * Both the seats tooltip and the payment breakdown have to disclose the same
 * thing, so which rows exist, what they are labelled and how their amounts are
 * derived is decided here once; each caller only renders them. Callers translate
 * `labelKey` with their own namespace and always render the legend alongside.
 *
 * The tax row is dropped when there is no tax to disclose: a tenant can be
 * configured tax-free, and then the row would read "IVA % ..." with no percent
 * to interpolate and an amount of 0.
 * @param {Object} options - The options for building the rows.
 * @param {{amount: number, tax: number}} [options.fee] - Fee as parsed by `parseFixedFee`.
 * @param {number} [options.seatCount] - Seats the fee is charged for, when `fee` holds per-seat amounts.
 * @param {number|string} [options.configuredTaxPercent] - Tenant `env.fixedFee.taxPercent`.
 * @param {number} [options.ticketTotal] - Total the rows must add up to; adds a leading ticket-value row.
 * @returns {Array<{labelKey: string, labelParams?: object, amount: number}>|null} Rows, or null when there is no fee.
 */
const getFixedFeeRows = ({ fee, seatCount = 1, configuredTaxPercent, ticketTotal } = {}) => {
  if (!fee?.amount) return null;

  const amount = fee.amount * seatCount;
  const tax = (fee.tax || 0) * seatCount;
  const rows = [];

  if (ticketTotal !== undefined) {
    rows.push({ labelKey: 'fixed_fee.ticket_price', amount: ticketTotal - amount - tax });
  }

  rows.push({ labelKey: 'fixed_fee.platform_usage', amount });

  if (tax > 0) {
    rows.push({
      labelKey: 'fixed_fee.platform_usage_tax',
      labelParams: { percent: getFixedFeeTaxPercent(fee, configuredTaxPercent) },
      amount: tax,
    });
  }

  return rows;
};

export default getFixedFeeRows;
